import { useQuery } from "@apollo/client"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { updateAlignment } from "./example"
import { asSign } from "./example_item"
import { MinimaticConfig } from "./minimatic"
import { type MinimaticAlignmentConfiguration } from "./minimatic/alignment"
import { graphql } from "@/api"
import { type SignTagExample_ExampleFragment } from "@/api/graphql"
import { type Fontdata, type Fontmap } from "@/common/client_data"
import { loadMinimatic, type MinimaticSignData, type MinimaticSignState } from "@/signomatic/app/minimatic"
import type Sign from "@/signomatic/app/sign"

const signdataDoc = graphql(`
  query ExampleItemSigndata {
    signomaticData {
      signData
      metaProducts
      fontData
    }
  }
`)

interface MinimaticConfigWrapperProps {
  example: SignTagExample_ExampleFragment
  admin: boolean
}

const MinimaticConfigWrapper: React.FC<MinimaticConfigWrapperProps> = ({ example, admin }) => {
  const [initAlignment, setInitAlignment] = useState<MinimaticAlignmentConfiguration | undefined>()
  const [minimatic, setMinimatic] = useState<Sign>()
  const exampleable = example.exampleable
  const sign = asSign(exampleable)

  const minimaticFonts = (example?.minimaticConfiguration?.fonts || [])
    .concat(sign?.fonts || [])
    .map(f => f.toLowerCase())
  const dataResult = useQuery(signdataDoc, { skip: !sign || !example?.minimatic, ssr: false })
  const signomaticData = useMemo(() => {
    const data = dataResult.data?.signomaticData

    if (!data) {
      return null
    }

    let fontdataCopy

    if (!admin) {
      const initFontData = data.fontData
      const filemap: Fontmap = {}

      for (const name in initFontData?.filemap) {
        if (minimaticFonts?.includes(name)) {
          filemap[name] = initFontData?.filemap[name]
        }
      }

      fontdataCopy = {
        filemap,
        disabled: initFontData.disabled,
        sets: initFontData.sets,
      }
    }

    return {
      signData: [...data.signData] as SignData[],
      fontData: (admin ? { ...data.fontData } : { ...fontdataCopy }) as Fontdata,
      metaProducts: [...data.metaProducts],
    }
  }, [dataResult.data?.signomaticData, minimaticFonts, admin])

  const saveConfig = useCallback(
    (align1: string | undefined, align2: string | undefined, align3: string | undefined) => {
      if (minimatic) {
        updateAlignment(
          minimatic,
          align1,
          align2,
          align3,
          initAlignment?.textAlign?.text1,
          initAlignment?.textAlign?.text2,
          initAlignment?.textAlign?.text3
        )

        setInitAlignment({ textAlign: { text1: align1, text2: align2, text3: align3 } })
      }
    },
    [minimatic, initAlignment]
  )

  useEffect(() => {
    async function load(sign: MinimaticSignState, data: MinimaticSignData) {
      const minimatic = await loadMinimatic(sign, data)
      setMinimatic(minimatic)
    }

    // Unload minimatic if disabling minimatic
    if (minimatic && !example?.minimatic) {
      setMinimatic(undefined)
    }

    if (!minimatic && sign && example?.minimatic && signomaticData) {
      load(sign, signomaticData).catch(error => {
        console.error(error)
      })
    }
  }, [minimatic, sign, signomaticData, example?.minimatic])

  if (!sign) {
    return
  }

  return (
    <MinimaticConfig
      exampleId={example.id}
      enabled={example?.minimatic}
      allowed={sign.minimaticAllowed}
      sign={sign}
      signomaticData={signomaticData}
      configuration={example?.minimaticConfiguration || { textAlign: initAlignment?.textAlign }}
      onSave={saveConfig}
    />
  )
}

export default MinimaticConfigWrapper
